/**
 * WorkPathTasksPanel (#714)
 *
 * Renders a booking's WorkPath tasks directly inside the expanded work-order
 * card in My Tasks, below the assigned services. Mirrors the inline-checklist
 * UX (InlineChecklistPanel / MechanicChecklistRow): checkbox verifications are
 * completed with one tap, while text / numeric / photo verifications open the
 * existing WorkPathTaskDialog.
 *
 * Contract notes (see useWorkPaths.ts):
 * - The by-booking GET echoes saved entry values (#826) and carries a per-task
 *   `assignedMechanicId` (#2386), so fill state and ownership both come from
 *   the query rather than a session-local map.
 * - The whole path stays visible even when tasks belong to someone else: on a
 *   car with two mechanics each needs to see the other's progress. Ownership
 *   is marked instead of filtered (#2510).
 * - Verification entry ids equal the verification ids on the task (the
 *   single-entry PATCH endpoint keys by verification id), so the checkbox
 *   one-tap uses `verification.id` as the entry id.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useAuthStore } from '@/stores/authStore';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  useBookingWorkPaths,
  useUpdateWorkPathEntry,
  type BookingWorkPath,
  type WorkPathTask,
  type WorkPathVerification,
} from '@/hooks/useWorkPaths';
import { useNotify } from '@/hooks/useNotify';
import { WorkPathTaskDialog } from '@/components/workorders/WorkPathTaskDialog';

/** A checkbox value counts as "filled" only when explicitly 'true'. */
function isFilled(value: string | null | undefined): boolean {
  return value != null && value !== '' && value !== 'false';
}

interface WorkPathTaskRowProps {
  workPathId: string;
  task: WorkPathTask;
  disabled: boolean;
  onSaveCheckbox: (workPathId: string, entryId: string, value: string) => void;
  onOpenTask: (workPathId: string, task: WorkPathTask) => void;
}

/** Maximum inline checkbox rows shown before deferring to the dialog. */
const MAX_VISIBLE_CHECKBOXES = 5;

function WorkPathTaskRow({
  workPathId,
  task,
  disabled,
  onSaveCheckbox,
  onOpenTask,
}: WorkPathTaskRowProps) {
  const { t } = useTranslation('workorders');
  const currentUserId = useAuthStore((s) => s.user?.id);

  /**
   * Whose task this is (#2510). The whole path stays visible — on a car with
   * two mechanics each needs to see what the other has done — but the rows
   * that are this mechanic's responsibility are marked, so "3/3" on someone
   * else's task is not mistaken for their own work being finished.
   */
  const isMine = task.assignedMechanicId != null && task.assignedMechanicId === currentUserId;
  const isSomeoneElses =
    task.assignedMechanicId != null && task.assignedMechanicId !== currentUserId;

  const verifications = useMemo<WorkPathVerification[]>(
    () => [...task.verifications].sort((a, b) => a.sortOrder - b.sortOrder),
    [task.verifications],
  );

  const checkboxVerifications = useMemo(
    () => verifications.filter((v) => v.type === 'checkbox'),
    [verifications],
  );
  const hasNonCheckbox = verifications.some((v) => v.type !== 'checkbox');

  // Persisted fill state from the GET (#826) — reload-safe, no session-local map.
  const completedCount = verifications.filter((v) => isFilled(v.value)).length;
  const totalCount = verifications.length;

  const visibleCheckboxes = checkboxVerifications.slice(0, MAX_VISIBLE_CHECKBOXES);
  const hiddenCount = checkboxVerifications.length - visibleCheckboxes.length;

  return (
    <Box
      data-testid="work-path-task-row"
      sx={{
        mb: 1,
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isMine ? 'primary.main' : 'divider',
        // Dimmed rather than hidden: a colleague's task is context, not noise.
        opacity: isSomeoneElses ? 0.6 : 1,
      }}
    >
      {/* Task header: name + per-task verification progress + open-dialog action */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.75}
        mb={0.5}
      >
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {task.name}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          {/*
            Without this the mechanic sees 3/3 and a full bar and reasonably
            concludes the task is finished — while it actually sits at
            awaiting_approval and holds the whole work path at in_progress
            (work-path-rollup.ts:56). Approval itself happens on the work
            order, where the supervisor has the context.
          */}
          {isMine && (
            <Chip
              size="small"
              color="primary"
              label={t('myTasks.workPathTaskMine')}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {task.status === 'awaiting_approval' && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('myTasks.workPathAwaitingApproval')}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            whiteSpace="nowrap"
            data-testid="work-path-task-row-progress"
          >
            {completedCount}/{totalCount}
          </Typography>
          <Tooltip title={t('myTasks.workPathTaskOpen')}>
            <IconButton
              size="small"
              aria-label={t('myTasks.workPathTaskOpen')}
              onClick={(e) => {
                e.stopPropagation();
                onOpenTask(workPathId, task);
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Inline one-tap checkbox verifications */}
      {visibleCheckboxes.map((verification) => {
        const checked = verification.value === 'true';
        return (
          <FormControlLabel
            key={verification.id}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            sx={{
              ml: -0.5,
              mr: 0,
              display: 'flex',
              minHeight: 44,
              alignItems: 'center',
            }}
            control={
              <Checkbox
                size="small"
                checked={checked}
                onChange={(e) =>
                  onSaveCheckbox(workPathId, verification.id, e.target.checked ? 'true' : 'false')
                }
                sx={{ p: 0.75 }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  textDecoration: checked ? 'line-through' : 'none',
                  color: checked ? 'text.disabled' : 'text.primary',
                  overflowWrap: 'anywhere',
                  minWidth: 0,
                }}
              >
                {verification.label}
              </Typography>
            }
          />
        );
      })}

      {hiddenCount > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 3.5, display: 'block', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenTask(workPathId, task);
          }}
        >
          {t('myTasks.workPathTaskMoreVerifications', { count: hiddenCount })}
        </Typography>
      )}

      {/* Text / numeric / photo verifications are completed via the dialog */}
      {hasNonCheckbox && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 3.5, display: 'block', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenTask(workPathId, task);
          }}
        >
          {t('myTasks.workPathTaskNoCheckbox')}
        </Typography>
      )}
    </Box>
  );
}

interface WorkPathGroupProps {
  workPath: BookingWorkPath;
  disabled: boolean;
  onSaveCheckbox: (workPathId: string, entryId: string, value: string) => void;
  onOpenTask: (workPathId: string, task: WorkPathTask) => void;
}

function WorkPathGroup({
  workPath,
  disabled,
  onSaveCheckbox,
  onOpenTask,
}: WorkPathGroupProps) {
  const isCompleted = workPath.status === 'completed';
  const overall =
    workPath.totalTasks > 0
      ? Math.round((workPath.completedTasks / workPath.totalTasks) * 100)
      : 0;

  const tasks = useMemo<WorkPathTask[]>(
    () => [...workPath.tasks].sort((a, b) => a.sortOrder - b.sortOrder),
    [workPath.tasks],
  );

  return (
    <Box
      data-testid="work-path-group"
      sx={{
        mb: 1,
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isCompleted ? 'success.200' : 'divider',
        bgcolor: isCompleted ? 'success.50' : 'transparent',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
        <AccountTreeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" fontWeight={600} noWrap>
          {workPath.name}
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <LinearProgress
          variant="determinate"
          value={overall}
          color={isCompleted ? 'success' : 'primary'}
          sx={{ flex: 1, height: 3, borderRadius: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {workPath.completedTasks}/{workPath.totalTasks}
        </Typography>
      </Box>

      {tasks.map((task) => (
        <WorkPathTaskRow
          key={task.id}
          workPathId={workPath.id}
          task={task}
          disabled={disabled}
          onSaveCheckbox={onSaveCheckbox}
          onOpenTask={onOpenTask}
        />
      ))}
    </Box>
  );
}

interface WorkPathTasksPanelProps {
  /** The work order / booking id used to fetch the booking's work path(s). */
  workOrderId: string;
}

export function WorkPathTasksPanel({ workOrderId }: WorkPathTasksPanelProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { data: workPaths, isLoading } = useBookingWorkPaths(workOrderId);
  const updateEntry = useUpdateWorkPathEntry();

  const [openTask, setOpenTask] = useState<{ workPathId: string; task: WorkPathTask } | null>(
    null,
  );

  // The by-booking GET now echoes persisted entry values (#826), so checkbox
  // state and progress come straight from the query. The mutation invalidates
  // that query, so a tap refetches the rolled-up state — no session-local map.
  const handleSaveCheckbox = (workPathId: string, entryId: string, value: string) => {
    updateEntry.mutate(
      { id: workPathId, bookingId: workOrderId, entryId, value },
      { onError: (error) => notify.error(describeApiError(error, tc, { fallback: t('myTasks.actionFailed') })) },
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          {t('myTasks.loadingDetails')}
        </Typography>
      </Box>
    );
  }

  if (!workPaths || workPaths.length === 0) {
    return null;
  }

  return (
    <>
      <Box>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ mt: 1, mb: 0.5, display: 'block', textTransform: 'uppercase' }}
        >
          {t('myTasks.workPathsTitle')}
        </Typography>

        {workPaths.map((workPath) => (
          <WorkPathGroup
            key={workPath.id}
            workPath={workPath}
            disabled={updateEntry.isPending}
            onSaveCheckbox={handleSaveCheckbox}
            onOpenTask={(workPathId, task) => setOpenTask({ workPathId, task })}
          />
        ))}
      </Box>

      <WorkPathTaskDialog
        workPathId={openTask?.workPathId ?? ''}
        bookingId={workOrderId}
        task={openTask?.task ?? null}
        onClose={() => setOpenTask(null)}
      />
    </>
  );
}
